/**
 * ScheduleWindowDialog: add or edit one weekday window (TRA-1426).
 *
 * The caller owns the save and mounts this only while it is open, keyed by
 * the window, so the fields start from that window without an effect. It
 * owns the save for the reason ConfirmDialog gives: a mutation started
 * inside a dialog loses its callbacks if the dialog unmounts mid-flight.
 *
 * Problems show after the first Save, then update as the fields change.
 * What needs two windows to judge (an overlap) is Baseten's to refuse; its
 * sentence arrives as a toast and the dialog stays open on it.
 */

import { useState } from 'react'

import type { ModelScheduleWindowUpdate, Weekday } from '@/hooks/useModels'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import {
  browserTimezone,
  draftFrom,
  parseWindowDraft,
  timezoneOptions,
  WEEKDAYS,
  type ExistingWindow,
  type WindowDraft,
  type WindowErrors,
  type WindowField,
} from './scheduleWindows'

interface ScheduleWindowDialogProps {
  /** The window being changed, or null to add one. */
  editing: ExistingWindow | null
  /** The environment's other windows; a name must be unique among them. */
  others: readonly ExistingWindow[]
  /** The environment's timezone, or the browser's before a window exists. */
  timezone: string
  isPending: boolean
  onClose: () => void
  onSave: (saved: ModelScheduleWindowUpdate, timezone: string) => void
}

/** `id` plus the ARIA wiring that reads a field's message with the field. */
function fieldProps(
  id: string,
  error: string | undefined,
): { id: string; 'aria-invalid': boolean; 'aria-describedby': string | undefined } {
  return {
    id,
    'aria-invalid': error !== undefined,
    'aria-describedby': error === undefined ? undefined : `${id}-error`,
  }
}

/** One hairline or solid chip per weekday; solid means the window runs that day. */
function DayPicker({
  days,
  error,
  disabled,
  onChange,
}: {
  days: readonly Weekday[]
  error: string | undefined
  disabled: boolean
  onChange: (days: Weekday[]) => void
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      <p id="window-days-label" className="text-sm font-medium leading-none">
        Days
      </p>
      <div
        className="flex flex-wrap gap-1"
        role="group"
        aria-labelledby="window-days-label"
        aria-describedby={error === undefined ? undefined : 'window-days-error'}
      >
        {WEEKDAYS.map((day) => {
          const on = days.includes(day.value)
          return (
            <Button
              key={day.value}
              type="button"
              size="sm"
              variant={on ? 'default' : 'outline'}
              aria-pressed={on}
              className="min-w-[3.25rem]"
              disabled={disabled}
              onClick={() => {
                onChange(on ? days.filter((d) => d !== day.value) : [...days, day.value])
              }}
            >
              {day.label}
            </Button>
          )
        })}
      </div>
      <FieldError id="window-days-error" message={error} />
    </div>
  )
}

function TextField({
  id,
  label,
  error,
  ...input
}: {
  id: string
  label: string
  error: string | undefined
} & Omit<React.ComponentProps<typeof Input>, 'id'>): React.JSX.Element {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input {...fieldProps(id, error)} {...input} />
      <FieldError id={`${id}-error`} message={error} />
    </div>
  )
}

function TimezoneField({
  value,
  environmentTimezone,
  disabled,
  onChange,
}: {
  value: string
  environmentTimezone: string
  disabled: boolean
  onChange: (timezone: string) => void
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      <Label htmlFor="window-timezone">Timezone</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id="window-timezone" aria-describedby="window-timezone-hint">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {timezoneOptions(environmentTimezone, browserTimezone()).map((zone) => (
            <SelectItem key={zone} value={zone}>
              {zone}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id="window-timezone-hint" className="text-xs text-muted-foreground">
        Every window on this model uses this timezone.
      </p>
    </div>
  )
}

export function ScheduleWindowDialog({
  editing,
  others,
  timezone,
  isPending,
  onClose,
  onSave,
}: ScheduleWindowDialogProps): React.JSX.Element {
  const [draft, setDraft] = useState<WindowDraft>(() => draftFrom(editing, timezone))
  const [submitted, setSubmitted] = useState(false)
  const parsed = parseWindowDraft(draft, editing, others)
  const errors: WindowErrors = submitted && parsed.kind === 'error' ? parsed.errors : {}

  function set<K extends WindowField>(field: K, value: WindowDraft[K]): void {
    setDraft((prev) => ({ ...prev, [field]: value }))
  }

  function submit(): void {
    setSubmitted(true)
    if (parsed.kind === 'ok') onSave(parsed.saved, parsed.timezone)
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Escape, the overlay and the corner X all land here; none of them
        // may dismiss a save that is still running.
        if (!next && !isPending) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing === null ? 'Add window' : 'Edit window'}</DialogTitle>
          <DialogDescription>
            The model keeps this replica range during the window. Its regular
            range applies the rest of the week.
          </DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <TextField
            id="window-name"
            label="Name"
            error={errors.name}
            value={draft.name}
            onChange={(e) => { set('name', e.target.value) }}
            placeholder="Weekday morning"
            disabled={isPending}
            autoFocus
          />
          <DayPicker
            days={draft.weekdays}
            error={errors.weekdays}
            disabled={isPending}
            onChange={(days) => { set('weekdays', days) }}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              id="window-start"
              label="Start"
              type="time"
              error={errors.start}
              value={draft.start}
              onChange={(e) => { set('start', e.target.value) }}
              disabled={isPending}
            />
            <TextField
              id="window-end"
              label="End"
              type="time"
              error={errors.end}
              value={draft.end}
              onChange={(e) => { set('end', e.target.value) }}
              disabled={isPending}
            />
            <TextField
              id="window-min-replica"
              label="Min replicas"
              type="number"
              min={0}
              max={64}
              error={errors.minReplica}
              value={draft.minReplica}
              onChange={(e) => { set('minReplica', e.target.value) }}
              disabled={isPending}
            />
            <TextField
              id="window-max-replica"
              label="Max replicas"
              type="number"
              min={1}
              max={64}
              error={errors.maxReplica}
              value={draft.maxReplica}
              onChange={(e) => { set('maxReplica', e.target.value) }}
              disabled={isPending}
            />
          </div>
          <TimezoneField
            value={draft.timezone}
            environmentTimezone={timezone}
            disabled={isPending}
            onChange={(zone) => { set('timezone', zone) }}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : 'Save window'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
